import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { YouTubeEmbed } from '../components/YouTubeEmbed'
import { contentHref, getContentById } from '../data/content'
import {
  LIVE_UNAVAILABLE_COPY_TA,
  NAVRATRI_GIVEAWAY,
  NAVRATRI_SEO,
  NAVRATRI_TRIAL,
  type NavratriGiveawayConfig,
  type NavratriState,
  type RoundConfig,
  googleFormEmbedUrl,
  isConfiguredUrl,
  isLiveRoundReady,
  isTrialFormReady,
  resolveFormFallbackUrl,
  navratriNowMs,
  resolveNavratriState,
  resolveRoundVideoId,
  trackNavratri,
} from '../data/navratriGiveaway'
import { SITE } from '../data/site'
import { useSeo } from '../hooks/useSeo'
import './NavratriGiveaway.css'

type Variant = 'production' | 'trial'

function liveRoundBadge(round: RoundConfig, state: NavratriState): string {
  if (state === 'ROUND_3_LIVE') return `${round.label} • FINAL ROUND • இப்போது திறந்துள்ளது`
  if (state === 'ROUND_2_LIVE') return `${round.label} • இப்போது திறந்துள்ளது`
  if (state === 'ROUND_1_LIVE') return `${round.label} • இப்போது திறந்துள்ளது`
  return `${round.label} • LIVE`
}

function statusPill(state: NavratriState, round: RoundConfig | null): { label: string; tone: string } {
  switch (state) {
    case 'TRIAL_LIVE':
      return { label: 'TRIAL • FORM OPEN NOW', tone: 'live' }
    case 'ROUND_1_LIVE':
    case 'ROUND_2_LIVE':
    case 'ROUND_3_LIVE':
      return { label: round ? liveRoundBadge(round, state) : 'LIVE', tone: 'live' }
    case 'LIVE_UNAVAILABLE':
      return { label: 'தற்காலிகமாக தயாராகிறது', tone: 'soon' }
    case 'BETWEEN_1_2':
      return { label: 'Round 2 — காலை 7:00க்கு திறக்கும்', tone: 'soon' }
    case 'BETWEEN_2_3':
      return { label: 'Round 3 — காலை 7:00க்கு திறக்கும்', tone: 'soon' }
    case 'COMPLETED':
      return { label: 'நிறைவடைந்தது', tone: 'closed' }
    default:
      return { label: 'விரைவில் தொடங்குகிறது', tone: 'soon' }
  }
}

function TrialCopy() {
  return (
    <div className="nr-panel">
      <h2>சோதனை Form இப்போது திறந்துள்ளது ✅</h2>
      <p>
        இது உண்மையான நவராத்திரி போட்டி பதிவு அல்ல. Website-ல் Form எப்படி தெரிகிறது, mobile-ல் scroll
        சரியாக உள்ளதா, Submit வேலை செய்கிறதா என்பதைச் சோதிக்க மட்டும் இந்த Trial Form
        பயன்படுத்தப்படுகிறது.
      </p>
      <ol className="nr-steps">
        <li>கீழே உள்ள Trial Form-ஐ முழுமையாக பாருங்கள்.</li>
        <li>Sample பதில்களை நிரப்புங்கள்.</li>
        <li>Submit செய்து Response Sheet-ல் entry வந்துள்ளதா சரிபார்க்கவும்.</li>
      </ol>
      <p className="nr-panel__meta">
        TRIAL முடியும் வரை இந்த Form date/time gate இல்லாமல் காண்பிக்கப்படும்.
      </p>
    </div>
  )
}

function ProductionStateCopy({
  state,
  config,
}: {
  state: NavratriState
  config: NavratriGiveawayConfig
}) {
  const r1 = config.rounds[0]

  if (state === 'LIVE_UNAVAILABLE') {
    return (
      <div className="nr-panel nr-panel--closed">
        <h2>போட்டி Form தயாராகிறது</h2>
        <p>{LIVE_UNAVAILABLE_COPY_TA}</p>
      </div>
    )
  }

  if (state === 'ROUND_1_LIVE') {
    return (
      <div className="nr-panel">
        <p className="nr-panel__eyebrow">நவராத்திரி Giveaway 2026</p>
        <h2>ROUND 1 — இன்றைய போட்டி 🎁</h2>
        <p>
          முதலில் கீழே உள்ள நாராயணி ஸ்துதி வீடியோவை முழுமையாக பார்த்து / கேளுங்கள். அதன் பிறகு கீழே
          உள்ள Round 1 கேள்விகளுக்கு பதில் அளித்து உங்கள் பதிவை சமர்ப்பிக்கவும்.
        </p>
      </div>
    )
  }

  if (
    state === 'ROUND_2_LIVE' ||
    state === 'BETWEEN_1_2' ||
    state === 'BETWEEN_2_3' ||
    state === 'ROUND_3_LIVE' ||
    state === 'COMPLETED'
  ) {
    return null
  }

  return (
    <div className="nr-panel">
      <h2>நவராத்திரி பரிசுப் போட்டி 2026</h2>
      <p className="nr-next">
        Round 1 — <strong>{r1.displayDateTa}</strong> தொடங்கும்
      </p>
      <p>அதுவரை தெய்வீக ஓசையின் நவராத்திரி சிறப்பு பாடல்களை கேட்டு மகிழுங்கள்.</p>
    </div>
  )
}

function formatIstPoint(iso: string) {
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return iso
  return new Intl.DateTimeFormat('ta-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(ms)
}

function GiveawayRules({ config }: { config: NavratriGiveawayConfig }) {
  const r2 = config.rounds[1]
  const r3 = config.rounds[2]
  return (
    <section className="nr-rules-board" aria-labelledby="nr-rules-title">
      <h2 id="nr-rules-title">விதிமுறைகள்</h2>
      <div className="nr-rules-summary">
        <p>🎁 தெய்வீக ஓசை – Navratri Giveaway 2026</p>
        <p>🥇 முதல் பரிசு: 5 கிராம் வெள்ளி மகாலட்சுமி நாணயம்</p>
        <ul>
          <li>Round 1 — முடிந்தது</li>
          <li>
            Round 2 — {formatIstPoint(r2.start)} → {formatIstPoint(r2.end)}
          </li>
          <li>
            Round 3 — {formatIstPoint(r3.start)} → {formatIstPoint(r3.end)}
          </li>
        </ul>
      </div>

      <details className="nr-rules">
        <summary>எப்படி கலந்து கொள்வது?</summary>
        <div className="nr-rules__body">
          <p>
            நவராத்திரி நாட்களில் Deiveega Oosai YouTube Channel-ல் 3 Special Devotional Videos
            வெளியாகும். ஒவ்வொரு Video-க்கும் ஒரு Giveaway Round இருக்கும்.
          </p>
          <ol>
            <li>அந்த Round-க்கான Video-வை பார்த்து / கேளுங்கள்.</li>
            <li>Video பற்றிய 3 எளிய MCQ Questions-க்கு பதில் அளியுங்கள்.</li>
            <li>அந்த Round திறந்திருக்கும் நேரத்தில் இந்த Navratri page-லேயே Entry Form-ஐ submit செய்யுங்கள்.</li>
          </ol>
          <p>Entry முழுமையாக இலவசம்.</p>
        </div>
      </details>

      <details className="nr-rules">
        <summary>முக்கிய விதிகள்</summary>
        <ul className="nr-rules__body">
          <li>மொத்தம் 3 Rounds; ஒவ்வொரு Round-லும் 3 Questions — மொத்தம் 9.</li>
          <li>Final Draw-க்கு மூன்று Rounds-லுமே கலந்து கொள்ள வேண்டும்.</li>
          <li>குறைந்தது 7/9 சரியான பதில்கள் இருக்க வேண்டும்.</li>
          <li>Entry Form அந்த Round card-ல் காட்டப்பட்ட நேரத்திற்கு மட்டும் open இருக்கும்.</li>
          <li>மூன்று Rounds-லுமே ஒரே Mobile Number பயன்படுத்த வேண்டும்.</li>
          <li>ஒரு Mobile Number-க்கு ஒரு Round-ல் ஒரு valid entry மட்டும்.</li>
          <li>Duplicate / invalid / incomplete entries disqualify செய்யப்படலாம்.</li>
          <li>Qualified participants-லிருந்து Winner random draw மூலம் தேர்வு செய்யப்படுவார்.</li>
          <li>Giveaway இந்தியாவில் தற்போது வசிக்கும் நபர்களுக்கு மட்டும்.</li>
        </ul>
      </details>

      <details className="nr-rules">
        <summary>Subscribe, Winner & Prize</summary>
        <div className="nr-rules__body">
          <p>
            🔔 Subscribe / Bell Notification eligibility-க்கு கட்டாயம் இல்லை. எந்த Round-ஐயும் miss
            பண்ணாமல் இருக்க Subscribe செய்து Notifications ON வைத்துக்கொள்ள பரிந்துரைக்கிறோம்.
          </p>
          <p>
            📱 Winner-ஐ Entry Form-ல் கொடுத்த Mobile Number மூலம் தொடர்பு கொள்வோம். Prize delivery
            details Winner-களிடம் மட்டும் பின்னர் பெறப்படும்.
          </p>
          <p>🎁 Prize-ஐ cash-ஆக மாற்ற முடியாது.</p>
        </div>
      </details>

      <details className="nr-rules">
        <summary>Privacy & Platform Disclaimer</summary>
        <div className="nr-rules__body">
          <p>
            🔐 Name & Mobile Number contest participation, verification, winner contact மற்றும் prize
            delivery purposes-க்காக மட்டுமே பயன்படுத்தப்படும்.
          </p>
          <p>
            ⚠️ இந்த Giveaway-வை Deiveega Oosai / MDAP Studios நடத்துகிறது. YouTube, Instagram, Facebook
            ஆகியவை இந்த Giveaway-ன் Sponsor அல்ல.
          </p>
          <p>🌐 Devotional songs, slokas & updates: deiveegaoosai.in</p>
        </div>
      </details>
    </section>
  )
}

function NavratriGiveawayView({ variant }: { variant: Variant }) {
  const isTrial = variant === 'trial'
  const config = isTrial ? NAVRATRI_TRIAL : NAVRATRI_GIVEAWAY
  const seo = isTrial ? NAVRATRI_SEO.trial : NAVRATRI_SEO.production
  const [nowMs, setNowMs] = useState(() => navratriNowMs())
  const lastTrackedState = useRef<string>('')

  useSeo({
    title: seo.title,
    description: seo.description,
    path: seo.path,
    skipTitleSuffix: true,
    robots: isTrial ? 'noindex, nofollow' : 'index, follow',
  })

  useEffect(() => {
    if (isTrial) return
    const w = window as Window & { __DO_NAVRATRI_R2_ENGINE__?: boolean }
    if (w.__DO_NAVRATRI_R2_ENGINE__) return
    w.__DO_NAVRATRI_R2_ENGINE__ = true

    const tick = () => setNowMs(navratriNowMs())
    const timer = window.setInterval(tick, config.checkIntervalMs)
    const boundaries = [config.rounds[2].start, config.rounds[2].end]
      .map((iso) => Date.parse(iso))
      .filter((target) => target - Date.now() > 0)
      .map((target) => window.setTimeout(tick, target - Date.now()))
    const onVisible = () => {
      if (!document.hidden) tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      w.__DO_NAVRATRI_R2_ENGINE__ = false
      window.clearInterval(timer)
      boundaries.forEach((id) => window.clearTimeout(id))
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [isTrial, config.checkIntervalMs, config.rounds])

  const resolved = useMemo(
    () => resolveNavratriState(config, nowMs, { forceTrialLive: isTrial }),
    [config, nowMs, isTrial],
  )

  let state = resolved.state
  const liveRound = resolved.liveRound

  const roundReady = isTrial ? isTrialFormReady(liveRound) : isLiveRoundReady(liveRound)

  if (
    !isTrial &&
    state === 'ROUND_1_LIVE' &&
    !roundReady
  ) {
    state = 'LIVE_UNAVAILABLE'
  }

  const showForm =
    (state === 'TRIAL_LIVE' || state === 'ROUND_1_LIVE') &&
    roundReady &&
    liveRound != null

  const videoId = showForm && liveRound ? resolveRoundVideoId(liveRound) : null
  const formEmbedSrc =
    showForm && liveRound && isTrial
      ? googleFormEmbedUrl(liveRound.formUrl)
      : showForm && liveRound
        ? googleFormEmbedUrl(liveRound.formUrl)
        : null

  const pill = statusPill(state, liveRound)

  useEffect(() => {
    const key = `${variant}:${state}`
    if (lastTrackedState.current === key) return
    lastTrackedState.current = key
    trackNavratri('navratri_page_view', { variant, path: seo.path, state })
  }, [variant, seo.path, state])

  useEffect(() => {
    if (showForm && liveRound) {
      trackNavratri('contest_form_visible', { round: liveRound.label })
    }
  }, [showForm, liveRound?.label])

  const r2 = config.rounds[1]
  const r2VideoId = resolveRoundVideoId(r2)
  const r2FormEmbed = isConfiguredUrl(r2.formUrl) ? googleFormEmbedUrl(r2.formUrl) : null
  const r2Fallback = resolveFormFallbackUrl(r2)
  const showR2Live = !isTrial && state === 'ROUND_2_LIVE'
  const showR2Upcoming = !isTrial && state === 'BETWEEN_1_2'
  const r2Ended = Date.parse(r2.end)
  const showR2LatestCard = !isTrial && r2VideoId != null && !Number.isNaN(r2Ended) && nowMs >= r2Ended

  const r3 = config.rounds[2]
  const r3VideoId = resolveRoundVideoId(r3)
  const r3FormEmbed = isConfiguredUrl(r3.formUrl) ? googleFormEmbedUrl(r3.formUrl) : null
  const r3Fallback = resolveFormFallbackUrl(r3)
  const showR3Live = !isTrial && state === 'ROUND_3_LIVE'
  const showR3Upcoming = !isTrial && state === 'BETWEEN_2_3'
  const showGiveawayDone = !isTrial && state === 'COMPLETED'
  const r3Ended = Date.parse(r3.end)
  const showR3LatestCard = !isTrial && r3VideoId != null && !Number.isNaN(r3Ended) && nowMs >= r3Ended
  const r3FormStyle = {
    '--form-height-mobile': `${r3.formHeightMobile}px`,
    '--form-height-tablet': `${r3.formHeightTablet}px`,
    '--form-height-desktop': `${r3.formHeightDesktop}px`,
  } as CSSProperties

  useEffect(() => {
    if (showR2Live && r2FormEmbed) trackNavratri('contest_form_visible', { round: 'ROUND 2' })
    if (showR3Live && r3FormEmbed) trackNavratri('contest_form_visible', { round: 'ROUND 3' })
  }, [showR2Live, r2FormEmbed, showR3Live, r3FormEmbed])

  const recommended = useMemo(() => {
    const fromSite = config.recommendedContentIds
      .map(getContentById)
      .filter(Boolean)
      .map((c) => ({
        key: c!.id,
        titleTa: c!.cardTitleTa ?? c!.titleTa,
        titleEn:
          c!.id === 'narayani-stuti' ? 'Sarva Mangala Mangalye • நவராத்திரி சிறப்பு' : c!.titleEn,
        badge: c!.id === 'narayani-stuti' ? 'புதியது' : '',
        to: contentHref(c!),
        videoId: c!.youtubeVideoId,
        youtubeOnly: false as const,
      }))
    const fromYt = config.recommendedYoutube.map((y) => ({
      key: y.videoId,
      titleTa: y.titleTa,
      titleEn: y.titleEn,
      badge: '',
      to: `https://www.youtube.com/watch?v=${y.videoId}`,
      videoId: y.videoId,
      youtubeOnly: true as const,
    }))
    const cards = [...fromSite, ...fromYt]
    if (showR2LatestCard && r2VideoId) {
      cards.unshift({
        key: 'round-2-latest',
        titleTa: 'Ya Devi Sarvabhuteshu / தேவி ஸ்துதி',
        titleEn: 'Round 2 • நவராத்திரி சிறப்பு',
        badge: '',
        to: r2.videoUrl,
        videoId: r2VideoId,
        youtubeOnly: true,
      })
    }
    if (showR3LatestCard && r3VideoId) {
      cards.unshift({
        key: 'round-3-latest',
        titleTa: 'சரஸ்வதி பாடல்',
        titleEn: 'குழந்தைகள் கல்வியில் சிறக்க • Round 3',
        badge: 'புதியது',
        to: r3.videoUrl,
        videoId: r3VideoId,
        youtubeOnly: true,
      })
    }
    return cards.slice(0, 8)
  }, [config, showR2LatestCard, showR3LatestCard, r2.videoUrl, r2VideoId, r3.videoUrl, r3VideoId])

  const formStyle: CSSProperties | undefined = liveRound
    ? ({
        '--form-height-mobile': `${liveRound.formHeightMobile}px`,
        '--form-height-tablet': `${liveRound.formHeightTablet}px`,
        '--form-height-desktop': `${liveRound.formHeightDesktop}px`,
      } as CSSProperties)
    : undefined

  const formDirectHref = liveRound ? resolveFormFallbackUrl(liveRound) : '#'
  const isRound1Live = !isTrial && state === 'ROUND_1_LIVE'

  return (
    <div className={`nr-page${isTrial ? ' nr-page--trial' : ''}`}>
      <section className="nr-hero">
        <div className="nr-hero__ornament" aria-hidden="true" />
        <div className="container nr-hero__inner">
          <Breadcrumbs
            items={[
              { label: 'முகப்பு', to: '/' },
              { label: isTrial ? 'Navratri Trial' : 'நவராத்திரி போட்டி' },
            ]}
          />
          {isTrial ? (
            <>
              <p className="nr-hero__eyebrow">Deiveega Oosai Navratri Giveaway — TRIAL MODE</p>
              <h1 className="nr-hero__title">நவராத்திரி பரிசுப் போட்டி 2026 — சோதனைப் பக்கம்</h1>
              <p className="nr-hero__sub">TRIAL FORM இப்போது திறந்துள்ளது • இது உண்மையான போட்டி பதிவு அல்ல</p>
            </>
          ) : (
            <>
              <p className="nr-hero__eyebrow">Deiveega Oosai Navratri Giveaway</p>
              <h1 className="nr-hero__title">நவராத்திரி பரிசுப் போட்டி 2026</h1>
              <p className="nr-hero__sub">
                Round 1 — {config.rounds[0].displayDateTa} • Round 2 — {config.rounds[1].displayDateTa} • Round 3 —{' '}
                {config.rounds[2].displayDateTa}
              </p>
            </>
          )}
          <p className={`nr-pill nr-pill--${pill.tone}`}>{pill.label}</p>
        </div>
      </section>

      <div className="container section nr-main">
        {showR2Upcoming && (
          <p className="nr-banner" id="round2-upcoming-banner">
            Round 2 — {r2.displayDateTa} தொடங்கும். அதுவரை நவராத்திரி பாடல்களைக் கேட்டு மகிழுங்கள்.
          </p>
        )}
        {showR3Upcoming && (
          <p className="nr-banner" id="round3-upcoming-note">
            Round 3 — {r3.displayDateTa} தொடங்கும். இறுதிச் சுற்று அக்டோபர் 20 காலை 7:00 மணிக்கு மூடப்படும்.
          </p>
        )}
        {showGiveawayDone && (
          <p className="nr-banner" id="giveaway-completed-note">
            நவராத்திரி பரிசுப் போட்டி நிறைவடைந்தது 🙏 {config.winnerAnnouncementTextTa}
          </p>
        )}
        {showR2Live && (
          <section className="nr-live" id="round2-live-module" aria-labelledby="round2-live-title">
            <div className="nr-live__badge">ROUND 2 • இப்போது திறந்துள்ளது</div>
            <h2 id="round2-live-title">ROUND 2 — இன்று போட்டி 🎁</h2>
            <p className="nr-next">16 அக்டோபர் காலை 7:00 மணி முதல் 17 அக்டோபர் காலை 7:00 மணி வரை</p>
            <p>
              முதலில் இன்றைய நவராத்திரி சிறப்பு வீடியோவை முழுமையாக பார்த்து / கேளுங்கள். அதன் பிறகு கீழே
              உள்ள 3 சுலபமான கேள்விகளுக்கு பதில் அளித்து உங்கள் Entry-ஐ Submit செய்யுங்கள்.
            </p>
            <p>இந்த Form 17 அக்டோபர் காலை 7:00 மணிக்கு மூடப்படும்.</p>
            {r2VideoId && (
              <>
                <YouTubeEmbed videoId={r2VideoId} title="Round 2 Navratri Special - Ya Devi Sarvabhuteshu" />
                <div className="btn-row nr-live__actions">
                  <a
                    className="btn btn--gold"
                    href={r2.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackNavratri('contest_video_click', { round: 'ROUND 2' })}
                  >
                    YouTube-ல் பார்க்க
                  </a>
                </div>
              </>
            )}
            <p className="nr-form-instruction">
              வீடியோவை பார்த்து / கேட்ட பிறகு கீழே உள்ள கேள்விகளுக்கு பதில் அளிக்கவும்.
            </p>
            {r2FormEmbed ? (
              <div className="do-contest-form do-google-form" style={formStyle}>
                <iframe
                  id="do-google-form"
                  src={r2FormEmbed}
                  title="Deiveega Oosai Navratri Giveaway ROUND 2 Form"
                  width="100%"
                  frameBorder={0}
                  marginHeight={0}
                  marginWidth={0}
                  scrolling="no"
                  loading="eager"
                  referrerPolicy="no-referrer-when-downgrade"
                >
                  Loading…
                </iframe>
              </div>
            ) : (
              <p className="nr-panel nr-panel--closed">{LIVE_UNAVAILABLE_COPY_TA}</p>
            )}
            <p className="do-form-fallback">
              <a
                id="do-form-fallback"
                href={r2Fallback}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackNavratri('contest_form_fallback_click', { round: 'ROUND 2' })}
              >
                Form திறக்க
              </a>
            </p>
          </section>
        )}
        {showR3Live && (
          <section className="nr-live" id="round3-live-module" aria-labelledby="round3-live-title">
            <div className="nr-live__badge">ROUND 3 • இறுதிச் சுற்று • இப்போது திறந்துள்ளது</div>
            <h2 id="round3-live-title">ROUND 3 — இறுதிச் சுற்று</h2>
            <p className="nr-next">அக்டோபர் 19, காலை 7:00 மணி முதல் அக்டோபர் 20, காலை 7:00 மணி வரை (IST).</p>
            <p>
              முதலில் இன்றைய சரஸ்வதி சிறப்பு பாடலை முழுமையாகக் கேளுங்கள். பின்னர் கீழே உள்ள Google
              Form-இல் 3 கேள்விகளுக்குப் பதிலளித்து உங்கள் பதிவைச் சமர்ப்பிக்கவும்.
            </p>
            {r3VideoId && (
              <>
                <YouTubeEmbed
                  videoId={r3VideoId}
                  title="குழந்தைகள் கல்வியில் சிறக்க — சரஸ்வதி பாடல் | Saraswathi Songs in Tamil"
                />
                <div className="btn-row nr-live__actions">
                  <a
                    className="btn btn--gold"
                    href={r3.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackNavratri('contest_video_click', { round: 'ROUND 3' })}
                  >
                    YouTube-ல் பார்க்க
                  </a>
                </div>
              </>
            )}
            <p className="nr-form-instruction">
              வீடியோவை பார்த்து / கேட்ட பிறகு கீழே உள்ள கேள்விகளுக்கு பதில் அளிக்கவும்.
            </p>
            {r3FormEmbed ? (
              <div className="do-contest-form do-google-form" style={r3FormStyle}>
                <iframe
                  id="do-google-form"
                  src={r3FormEmbed}
                  title="Deiveega Oosai Navratri Giveaway ROUND 3 Form"
                  width="100%"
                  frameBorder={0}
                  marginHeight={0}
                  marginWidth={0}
                  scrolling="no"
                  loading="eager"
                  referrerPolicy="no-referrer-when-downgrade"
                >
                  Loading…
                </iframe>
              </div>
            ) : (
              <p className="nr-panel nr-panel--closed">{LIVE_UNAVAILABLE_COPY_TA}</p>
            )}
            <p className="do-form-fallback">
              <a
                id="do-form-fallback"
                href={r3Fallback}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackNavratri('contest_form_fallback_click', { round: 'ROUND 3' })}
              >
                Form தனியாகத் திறக்க
              </a>
            </p>
          </section>
        )}

        {isTrial ? <TrialCopy /> : <ProductionStateCopy state={state} config={config} />}

        {showForm && liveRound && formEmbedSrc && (
          <>
            {(videoId || isTrial) && (
              <section className="nr-live" aria-labelledby="contest-video-title">
                <div className="nr-live__badge">
                  {isTrial ? 'TRIAL • OPTIONAL VIDEO' : liveRoundBadge(liveRound, state)}
                </div>
                {videoId && (
                  <>
                    <h2 id="contest-video-title" className="nr-section-title">
                      {isTrial
                        ? 'மாதிரி வீடியோ (விருப்பத்தேர்வு)'
                        : isRound1Live
                          ? 'நாராயணி ஸ்துதி — போட்டி வீடியோ'
                          : 'போட்டி வீடியோ'}
                    </h2>
                    <YouTubeEmbed
                      videoId={videoId}
                      title={
                        isRound1Live
                          ? 'நவராத்திரி ஸ்பெஷல் — நாராயணி ஸ்துதி | Sarva Mangala Mangalye'
                          : `${liveRound.label} video`
                      }
                    />
                    <div className="btn-row nr-live__actions">
                      <a
                        className="btn btn--gold"
                        href={liveRound.videoUrl.startsWith('http') ? liveRound.videoUrl : `https://www.youtube.com/watch?v=${videoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackNavratri('contest_video_click', { round: liveRound.label })}
                      >
                        {isRound1Live ? 'YouTube-ல் பார்க்க' : 'YouTube-ல் திறந்து பாருங்கள்'}
                      </a>
                    </div>
                  </>
                )}
              </section>
            )}

            <section
              className="do-navratri-form-section"
              aria-labelledby="contest-form-title"
              style={formStyle}
            >
              {!isTrial && !isRound1Live && (
                <p className="nr-form-instruction">
                  வீடியோவை முழுமையாகப் பார்த்த பிறகே Form-ல் பதிலளிக்கவும்.
                </p>
              )}
              <h2 id="contest-form-title" className="nr-section-title">
                உங்கள் பதிலை இங்கே பதிவு செய்யுங்கள்
              </h2>
              <div className={`do-contest-form do-google-form${isTrial ? ' do-google-form--trial' : ''}`}>
                <iframe
                  id="do-google-form"
                  src={formEmbedSrc}
                  title={
                    isTrial
                      ? 'Deiveega Oosai Navratri Giveaway Trial Form'
                      : `Deiveega Oosai Navratri Giveaway ${liveRound.label} Form`
                  }
                  width="100%"
                  frameBorder={0}
                  marginHeight={0}
                  marginWidth={0}
                  scrolling="no"
                  loading="eager"
                  referrerPolicy="no-referrer-when-downgrade"
                >
                  Loading…
                </iframe>
              </div>
              <p className="do-form-fallback">
                {isRound1Live ? (
                  <>
                    <a
                      id="do-form-fallback"
                      href={formDirectHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackNavratri('contest_form_fallback_click', { round: liveRound.label })}
                    >
                      Form திறக்கவில்லை என்றால் இங்கே தொடுங்கள்
                    </a>
                  </>
                ) : (
                  <>
                    Form சரியாக தெரியவில்லையா?{' '}
                    <a
                      id="do-form-fallback"
                      href={formDirectHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackNavratri('contest_form_fallback_click', { round: liveRound.label })}
                    >
                      Form தனியாகத் திறக்க
                    </a>
                  </>
                )}
              </p>
              <aside className="nr-privacy" role="note">
                <p>{config.privacyTextTa}</p>
                <p>{config.rulesSummaryTa}</p>
              </aside>
            </section>
          </>
        )}

        {!showForm && (
          <aside className="nr-privacy nr-privacy--standalone" role="note">
            <p>{config.rulesSummaryTa}</p>
          </aside>
        )}

        <GiveawayRules config={config} />

        <section className="nr-recs" aria-labelledby="nr-recs-title">
          <h2 id="nr-recs-title" className="nr-section-title">
            {showForm ? 'மேலும் கேட்டு மகிழுங்கள்' : 'இதற்கிடையில் கேட்டு மகிழுங்கள்'}
          </h2>
          <p className="nr-recs__sub">நவராத்திரி சிறப்பு பாடல்கள் & ஸ்துதிகள்</p>
          <div className="nr-recs__grid">
            {recommended.map((item) =>
              item.youtubeOnly ? (
                <a
                  key={item.key}
                  className="nr-rec"
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackNavratri('navratri_content_click', { id: item.key })}
                >
                  <div className="nr-rec__media">
                    <img
                      src={`https://i.ytimg.com/vi/${item.videoId}/hqdefault.jpg`}
                      alt={item.titleTa}
                      width={480}
                      height={270}
                      loading="lazy"
                    />
                  </div>
                  <div className="nr-rec__body">
                    {item.badge ? <span className="nr-rec__badge">{item.badge}</span> : null}
                    <h3>{item.titleTa}</h3>
                    <span>{item.titleEn}</span>
                  </div>
                </a>
              ) : (
                <Link
                  key={item.key}
                  className="nr-rec"
                  to={item.to}
                  onClick={() => trackNavratri('navratri_content_click', { id: item.key })}
                >
                  <div className="nr-rec__media">
                    {item.videoId ? (
                      <img
                        src={`https://i.ytimg.com/vi/${item.videoId}/hqdefault.jpg`}
                        alt={item.titleTa}
                        width={480}
                        height={270}
                        loading="lazy"
                      />
                    ) : (
                      <div className="nr-rec__ph" aria-hidden="true" />
                    )}
                  </div>
                  <div className="nr-rec__body">
                    {item.badge ? <span className="nr-rec__badge">{item.badge}</span> : null}
                    <h3>{item.titleTa}</h3>
                    <span>{item.titleEn}</span>
                  </div>
                </Link>
              ),
            )}
          </div>
          <div className="btn-row nr-recs__ctas">
            <Link className="btn btn--outline" to="/#navratri-collection">
              அனைத்து நவராத்திரி பாடல்களும்
            </Link>
            <Link className="btn btn--outline" to="/deiveegam-theriyuma">
              தெய்வீகம் தெரியுமா?
            </Link>
            <a className="btn btn--ghost" href={SITE.youtubeUrl} target="_blank" rel="noopener noreferrer">
              YouTube சேனல்
            </a>
          </div>
        </section>
      </div>
    </div>
  )
}

export function NavratriGiveawayPage() {
  return <NavratriGiveawayView variant="production" />
}

export function NavratriTrialPage() {
  return <NavratriGiveawayView variant="trial" />
}
